// AutoExposure.tsx — In planet mode, adapt the renderer's tone-mapping exposure to
// the focused planet's stellar flux so a world close to its star doesn't wash out to
// white, and a far/dim one stays clearly visible. The star's point light has decay 2,
// so irradiance ∝ 1/a²; normalizing the lit hemisphere to a steady level therefore
// means exposure ∝ a² (referenced to REF_A → exposure 1). Galaxy/system views keep
// the default exposure of 1. The change ramps smoothly so flying in doesn't pop.
import { useFrame, useThree } from '@react-three/fiber';
import type { StarSystem } from '../types';

// Orbit radius (scene units) that maps to neutral exposure 1. Closer worlds expose
// down (so they don't blow out), farther ones expose up (so they stay readable).
const REF_A = 3.0;
const MIN_EXPOSURE = 0.5;
const MAX_EXPOSURE = 1.9;

interface Props {
  systems: StarSystem[];
  focusedPlanetId: string | null;
}

export function AutoExposure({ systems, focusedPlanetId }: Props) {
  const gl = useThree((state) => state.gl);

  // Target exposure for the focused planet (1 when not in planet mode).
  let target = 1;
  if (focusedPlanetId) {
    for (const s of systems) {
      const p = s.planets.find((pl) => pl.id === focusedPlanetId);
      if (p) {
        target = Math.min(MAX_EXPOSURE, Math.max(MIN_EXPOSURE, (p.semiMajorAxis / REF_A) ** 2));
        break;
      }
    }
  }

  useFrame((_, delta) => {
    const cur = gl.toneMappingExposure;
    gl.toneMappingExposure = cur + (target - cur) * Math.min(1, delta * 2.5);
  });

  return null;
}
